/**
 * Read-only display panels for the persisted Shopify listing payload on
 * the wheel and tire detail pages. Surfaces what will be pushed to
 * Shopify (and from there to Google Merchant Center) without offering
 * any edit affordance, push button, or DB-mutating action.
 *
 * Four panels per product:
 *   1. ListingSeoPanel        — title/handle/description/tags/SEO + ready status
 *   2. GoogleMerchantPanel    — GMC-bound identifiers + COO + HS code + price
 *   3. MetafieldsPanel        — metafields_json + Shopify metafields preview
 *   4. VariantsPanel          — sibling rows that become Shopify variants
 *
 * Both wheel and tire detail pages render the same four panels with a
 * thin discriminator (`kind`) so the small per-table differences (size
 * column, load_rating vs load_index, etc.) stay localized here.
 *
 * No edit buttons, no Shopify mutations, no DB writes. The only network
 * call originating in this file is a GET for variant siblings.
 */
import { useQuery } from '@tanstack/react-query';
import {
  Card, BlockStack, InlineStack, Text, Badge, Box, Banner, Spinner, Divider,
} from '@shopify/polaris';
import type { Wheel, WheelVariantSibling } from '../api/wheels';
import type { TireProductFull, TireVariantSibling } from '../api/tires';
import { getWheelVariants } from '../api/wheels';
import { getTireVariants } from '../api/tires';

type WheelKind = { kind: 'wheel'; product: Wheel };
type TireKind = { kind: 'tire'; product: TireProductFull };
type Props = WheelKind | TireKind;

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function readyTone(status: string | null | undefined): 'success' | 'warning' | 'critical' | undefined {
  if (status === 'ready') return 'success';
  if (status === 'warning') return 'warning';
  if (status === 'blocked' || status === 'excluded') return 'critical';
  return undefined;
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <Text as="span" tone="subdued">—</Text>;
  return <Badge tone={readyTone(status)}>{status}</Badge>;
}

function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <Box minWidth="220px">
      <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
      <Box paddingBlockStart="050">
        {mono ? (
          <code style={{ fontSize: 12, wordBreak: 'break-word' }}>{children}</code>
        ) : (
          <Text as="p" variant="bodyMd">{children as any}</Text>
        )}
      </Box>
    </Box>
  );
}

function dash(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

// ─── 1. Listing & SEO ──────────────────────────────────────────────────────
export function ListingSeoPanel(props: Props) {
  const p = props.product as any;
  const tags: string[] = Array.isArray(p.product_tags) ? p.product_tags : [];
  const collTags: string[] = Array.isArray(p.collection_tags) ? p.collection_tags : [];
  const reasons = p.shopify_ready_reasons || {};
  const blocking: string[] = Array.isArray(reasons.blocking_reasons) ? reasons.blocking_reasons : [];
  const warnings: string[] = Array.isArray(reasons.warnings) ? reasons.warnings : [];

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">Listing &amp; SEO</Text>
          <StatusBadge status={p.shopify_ready_status} />
        </InlineStack>
        <Text as="p" variant="bodySm" tone="subdued">
          Persisted Shopify listing payload — title, handle, SEO, description,
          and tag set. Read-only display. Edits go through the listing
          regenerator, not direct PATCH.
        </Text>

        <InlineStack gap="400" wrap>
          <Field label="Shopify title">{dash(p.shopify_title)}</Field>
          <Field label="Shopify handle" mono>{dash(p.shopify_handle)}</Field>
        </InlineStack>

        <InlineStack gap="400" wrap>
          <Field label="SEO title">{dash(p.seo_title)}</Field>
          <Field label="Description source">
            {p.description_source ? <Badge>{p.description_source}</Badge> : '—'}
          </Field>
        </InlineStack>

        <Box>
          <Text as="p" variant="bodySm" tone="subdued">SEO description</Text>
          <Box paddingBlockStart="050">
            <Text as="p" variant="bodyMd">{dash(p.seo_description)}</Text>
            {p.seo_description && (
              <Text as="p" variant="bodySm" tone="subdued">
                {p.seo_description.length} chars
                {p.seo_description.length > 155 ? ' (over 155 — Google may truncate)' : ''}
              </Text>
            )}
          </Box>
        </Box>

        <Box>
          <Text as="p" variant="bodySm" tone="subdued">Product description (HTML)</Text>
          <Box paddingBlockStart="050" background="bg-surface-secondary" padding="300" borderRadius="200">
            {p.product_description ? (
              <pre style={{ margin: 0, fontSize: 11, maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {p.product_description}
              </pre>
            ) : (
              <Text as="p" tone="subdued">—</Text>
            )}
          </Box>
        </Box>

        <Divider />

        <Box>
          <Text as="p" variant="bodySm" tone="subdued">Product tags ({tags.length})</Text>
          <Box paddingBlockStart="100">
            {tags.length === 0 ? (
              <Text as="p" tone="subdued">—</Text>
            ) : (
              <InlineStack gap="100" wrap>
                {tags.map((t, i) => <Badge key={i}>{t}</Badge>)}
              </InlineStack>
            )}
          </Box>
        </Box>

        <Box>
          <Text as="p" variant="bodySm" tone="subdued">Collection tags ({collTags.length})</Text>
          <Box paddingBlockStart="100">
            {collTags.length === 0 ? (
              <Text as="p" tone="subdued">—</Text>
            ) : (
              <InlineStack gap="100" wrap>
                {collTags.map((t, i) => <Badge key={i} tone="info">{t}</Badge>)}
              </InlineStack>
            )}
          </Box>
        </Box>

        {(blocking.length > 0 || warnings.length > 0) && (
          <>
            <Divider />
            <Text as="p" variant="bodySm" tone="subdued">Push-gate reasons</Text>
            {blocking.length > 0 && (
              <Banner tone="critical" title="Blocking">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {blocking.map((r, i) => <li key={i}><code style={{ fontSize: 11 }}>{r}</code></li>)}
                </ul>
              </Banner>
            )}
            {warnings.length > 0 && (
              <Banner tone="warning" title="Warnings">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {warnings.map((r, i) => <li key={i}><code style={{ fontSize: 11 }}>{r}</code></li>)}
                </ul>
              </Banner>
            )}
          </>
        )}
      </BlockStack>
    </Card>
  );
}

// ─── 2. Google / Merchant ──────────────────────────────────────────────────
export function GoogleMerchantPanel(props: Props) {
  const p = props.product as any;
  const isWheel = props.kind === 'wheel';
  const mpn = p.mfg_part_number ?? null;
  const sku = p.sku ?? p.vendor_sku ?? null;
  const upc = p.upc ?? null;
  const priceCents = isWheel ? p.effective_price_cents : p.price_cents;
  const mapCents = p.map_price_cents ?? null;

  const productCategory = p.google_product_category
    ?? (isWheel
      ? 'Vehicles & Parts > Vehicle Parts & Accessories > Motor Vehicle Parts > Motor Vehicle Wheel Systems > Motor Vehicle Wheels'
      : 'Vehicles & Parts > Vehicle Parts & Accessories > Motor Vehicle Parts > Motor Vehicle Wheel Systems > Motor Vehicle Tires');
  const productType = p.product_type ?? (isWheel ? 'Wheels' : 'Tires');

  const availability = (() => {
    const status = isWheel ? p.inventory_status : null;
    if (status === 'fresh_in_stock') return { label: 'in_stock', tone: 'success' as const };
    if (status === 'fresh_oos' || p.install_speed === 'out_of_stock') return { label: 'out_of_stock', tone: 'critical' as const };
    if (status === 'stale') return { label: 'stale', tone: 'warning' as const };
    if (status === 'unknown') return { label: 'unknown', tone: undefined };
    return { label: dash(p.install_speed), tone: undefined };
  })();

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h3" variant="headingMd">Google / Merchant feed</Text>
        <Text as="p" variant="bodySm" tone="subdued">
          Identifiers and compliance fields that flow into the Google Merchant
          Center feed. Missing values trigger GMC warnings (non-blocking) or
          push-gate blocks. Read-only.
        </Text>
        <InlineStack gap="400" wrap>
          <Field label="Brand">{dash(p.brand)}</Field>
          <Field label="MPN" mono>{dash(mpn)}</Field>
          <Field label="SKU" mono>{dash(sku)}</Field>
          <Field label="GTIN / UPC" mono>{dash(upc)}</Field>
        </InlineStack>
        <InlineStack gap="400" wrap>
          <Field label="Google product category">{productCategory}</Field>
          <Field label="Product type">{productType}</Field>
        </InlineStack>
        <InlineStack gap="400" wrap>
          <Field label="Country of origin">
            {p.country_of_origin ? <Badge>{p.country_of_origin}</Badge> : <Badge tone="warning">missing</Badge>}
          </Field>
          <Field label="HS code" mono>
            {p.hs_code ? p.hs_code : <Badge tone="warning">missing</Badge>}
          </Field>
        </InlineStack>
        <Divider />
        <InlineStack gap="400" wrap>
          <Field label="Price">{dollars(priceCents)}</Field>
          <Field label="MAP">{dollars(mapCents)}</Field>
          <Field label="MSRP">{dollars(p.msrp_cents)}</Field>
          <Field label="Availability">
            {availability.tone
              ? <Badge tone={availability.tone}>{availability.label}</Badge>
              : <Badge>{availability.label}</Badge>}
          </Field>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ─── 3. Metafields ─────────────────────────────────────────────────────────
export function MetafieldsPanel(props: Props) {
  const p = props.product as any;
  const meta = p.metafields_json && typeof p.metafields_json === 'object' ? p.metafields_json : null;
  const entries = meta ? Object.entries(meta) : [];

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingMd">Metafields</Text>
        <Text as="p" variant="bodySm" tone="subdued">
          Persisted metafields_json — the exact payload that becomes Shopify
          product metafields on push (namespace + key + value, JSON-encoded).
          Read-only.
        </Text>
        {entries.length === 0 ? (
          <Banner tone="info">
            No metafields persisted yet. Will populate when the listing
            generator runs against this product.
          </Banner>
        ) : (
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <BlockStack gap="100">
              {entries.map(([k, v], i) => (
                <InlineStack key={i} gap="200" blockAlign="start" wrap>
                  <Box minWidth="240px">
                    <code style={{ fontSize: 12, wordBreak: 'break-word' }}>{k}</code>
                  </Box>
                  <Box>
                    <Text as="span" variant="bodySm">
                      {typeof v === 'string' ? v : JSON.stringify(v)}
                    </Text>
                  </Box>
                </InlineStack>
              ))}
            </BlockStack>
          </Box>
        )}
        <Box>
          <Text as="p" variant="bodySm" tone="subdued">Raw JSON</Text>
          <Box paddingBlockStart="050" background="bg-surface-secondary" padding="300" borderRadius="200">
            <pre style={{ margin: 0, fontSize: 11, maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {meta ? JSON.stringify(meta, null, 2) : '—'}
            </pre>
          </Box>
        </Box>
      </BlockStack>
    </Card>
  );
}

// ─── 4. Variants ───────────────────────────────────────────────────────────
export function VariantsPanel(props: Props) {
  const id = props.product.id;
  const isWheel = props.kind === 'wheel';
  const queryFn = isWheel
    ? () => getWheelVariants(id).then((r) => r.variants as (WheelVariantSibling | TireVariantSibling)[])
    : () => getTireVariants(id).then((r) => r.variants as (WheelVariantSibling | TireVariantSibling)[]);
  const q = useQuery({
    queryKey: [isWheel ? 'wheel-variants' : 'tire-variants', id],
    queryFn,
    enabled: id != null,
  });

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h3" variant="headingMd">Variants</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {isWheel
                ? 'Sibling wheels with the same brand/model/finish — these become Shopify variants on a single grouped product.'
                : 'Sibling tires with the same brand/model — these become Shopify variants on a single grouped product.'}
            </Text>
          </BlockStack>
          <Badge tone="info">{`${q.data?.length ?? 0} variants`}</Badge>
        </InlineStack>

        {q.isLoading && <Spinner accessibilityLabel="Loading variants" size="small" />}
        {q.error && <Banner tone="critical">{(q.error as Error).message}</Banner>}
        {q.data && q.data.length === 0 && (
          <Banner tone="info">No sibling variants — this is the only row in its grouping.</Banner>
        )}
        {q.data && q.data.length > 0 && (
          <Box background="bg-surface-secondary" padding="200" borderRadius="200">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={th}>Image</th>
                  <th style={th}>SKU</th>
                  <th style={th}>{isWheel ? 'Size' : 'Size'}</th>
                  {isWheel && <th style={th}>Offset</th>}
                  {isWheel && <th style={th}>Bolt pattern</th>}
                  {!isWheel && <th style={th}>Load / speed</th>}
                  <th style={th}>Price</th>
                  <th style={th}>Inv</th>
                  <th style={th}>Ready</th>
                  <th style={th}>Pushed?</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((v: any) => {
                  const img = isWheel ? (v.image_thumbnail || v.image_small || v.image_large) : v.image_url;
                  const size = isWheel
                    ? (v.diameter_in && v.width_in ? `${v.diameter_in}x${v.width_in}` : '—')
                    : (v.tire_size_full || v.size_display || '—');
                  const inv = isWheel
                    ? v.effective_install_speed
                    : (v.install_speed
                       ?? (((v.qty_local ?? 0) + (v.qty_local_plus ?? 0) + (v.qty_nationwide ?? 0)) > 0 ? 'in_stock' : '—'));
                  const price = isWheel ? v.effective_price_cents : v.price_cents;
                  return (
                    <tr key={v.id} style={{ borderTop: '1px solid var(--p-color-border)' }}>
                      <td style={td}>
                        {img ? <img src={img} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ opacity: 0.4 }}>—</span>}
                      </td>
                      <td style={tdMono}>{v.sku}</td>
                      <td style={td}>{size}</td>
                      {isWheel && <td style={td}>{v.offset_mm == null ? '—' : `ET${v.offset_mm >= 0 ? '+' : ''}${v.offset_mm}`}</td>}
                      {isWheel && <td style={td}>{(v.bolt_patterns_canonical || []).join(' / ') || '—'}</td>}
                      {!isWheel && <td style={td}>{[v.load_index, v.speed_rating].filter(Boolean).join(' ') || '—'}</td>}
                      <td style={td}>{dollars(price)}</td>
                      <td style={td}>{dash(inv)}</td>
                      <td style={td}><StatusBadge status={v.shopify_ready_status} /></td>
                      <td style={td}>{v.shopify_product_id ? <Badge tone="success">yes</Badge> : <Badge>no</Badge>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
}

const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600, fontSize: 11, color: 'var(--p-color-text-subdued)' };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'middle' };
const tdMono: React.CSSProperties = { ...td, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 };
